import apiEndpoint from '../components/config/data';
import { willTokenExpire, isTokenExpired, extractUserFromJWT } from './jwt';

/**
 * Utility function to get a cookie value by name with proper security handling
 * @param {string} name - The name of the cookie
 * @returns {string|null} The cookie value or null if not found
 */
function getCookie(name) {
  // Input validation
  if (!name || typeof name !== 'string') {
    return null;
  }

  // Sanitize cookie name to prevent injection
  const sanitizedName = name.replace(/[^a-zA-Z0-9_-]/g, '');
  if (sanitizedName !== name) {
    console.warn('Cookie name contains invalid characters:', name);
    return null;
  }

  try {
    // Parse cookies safely
    const cookies = document.cookie.split(';');

    for (let cookie of cookies) {
      const trimmedCookie = cookie.trim();
      if (trimmedCookie.startsWith(sanitizedName + '=')) {
        const cookieValue = trimmedCookie.substring(sanitizedName.length + 1);

        // URL decode the cookie value safely
        try {
          return decodeURIComponent(cookieValue);
        } catch (decodeError) {
          // If decoding fails, return the raw value (might be unencoded)
          console.warn(
            'Failed to decode cookie value, using raw value:',
            decodeError
          );
          return cookieValue;
        }
      }
    }

    return null;
  } catch (error) {
    console.error('Error parsing cookies:', error);
    return null;
  }
}

/**
 * Get the authorization header value for API requests
 * @returns {string} The authorization header value
 */
export const getAuthHeader = () => {
  const hasBlid = getCookie('PA_BLID'); // PA_BLID is only set during SSO flow
  const token = localStorage.getItem('access');

  // If we have PA_BLID but no access token, we're using SSO with HttpOnly PA_AUTH cookies
  // Don't send Authorization header - the browser will automatically send the HttpOnly cookie
  if (hasBlid && !token) {
    return '';
  }

  if (!token) return '';

  // For JWT tokens, send in Authorization header
  return `Bearer ${token}`;
};

/**
 * Service to handle JWT token operations
 */
class TokenService {
  constructor() {
    this.refreshInProgress = false;
    this.refreshInterval = null;
    this.visibilityChangeHandler = this.handleVisibilityChange.bind(this);
    this.isPageActive = true;
    this.onUserInfoUpdate = null; // Callback for user info updates
    this.onUserIdUpdate = null; // Callback for user ID updates
  }

  /**
   * Set callbacks for user info updates
   * @param {Function} onUserInfoUpdate - Callback to fetch user info
   * @param {Function} onUserIdUpdate - Callback to set user ID
   */
  setCallbacks(onUserInfoUpdate, onUserIdUpdate) {
    this.onUserInfoUpdate = onUserInfoUpdate;
    this.onUserIdUpdate = onUserIdUpdate;
  }

  /**
   * Initialize the token refresh service
   * @param {boolean} oktaSsoAuthEnabled - Whether the oktaSsoAuth feature flag is enabled
   */
  initialize(oktaSsoAuthEnabled = false) {
    // Check for PABE_JWT cookie first (only if feature flag is enabled)
    this.checkForCookieToken(oktaSsoAuthEnabled);

    // Add visibility change listener
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);

    // Start the refresh check interval
    this.startRefreshTimer();
  }

  /**
   * Check for PA_AUTH or PABE_JWT cookie and set it as the access token if found
   * Only runs if oktaSsoAuth feature flag is enabled
   * @param {boolean} oktaSsoAuthEnabled - Whether the oktaSsoAuth feature flag is enabled
   */
  checkForCookieToken(oktaSsoAuthEnabled = false) {
    // Check if oktaSsoAuth feature flag is enabled
    console.log('oktaSsoAuthEnabled - checkForCookieToken', oktaSsoAuthEnabled);
    if (!oktaSsoAuthEnabled) {
      return; // Feature flag is disabled, skip cookie check
    }

    // Only check for cookie if we don't already have tokens in localStorage
    const existingAccessToken = localStorage.getItem('access');
    console.log(
      'existingAccessToken - checkForCookieToken',
      existingAccessToken
    );
    if (existingAccessToken) {
      return; // Already have a token, no need to check cookie
    }

    // Check for PA_AUTH cookie first (new unified auth), then fall back to PABE_JWT
    let cookieToken = getCookie('PA_AUTH');
    let authType = 'pa_auth';
    console.log('cookieToken - PA_AUTH', cookieToken);

    // If PA_AUTH cookie is not readable (likely HttpOnly), check if we have PA_BLID
    // PA_BLID is only set during SSO flow and indicates we're using HttpOnly cookies
    if (!cookieToken) {
      const hasBlid = getCookie('PA_BLID');

      if (oktaSsoAuthEnabled && hasBlid) {
        console.log(
          'SSO enabled with PA_BLID cookie - using HttpOnly PA_AUTH cookie'
        );

        // Trigger user info fetch to validate the HttpOnly cookie
        if (this.onUserInfoUpdate) {
          this.onUserInfoUpdate();
        }
        return;
      }

      // Fall back to checking PABE_JWT
      cookieToken = getCookie('PABE_JWT');
      authType = 'pabe_jwt';
    }

    if (cookieToken) {
      try {
        console.log(
          `Found ${authType === 'pa_auth' ? 'PA_AUTH' : 'PABE_JWT'} cookie`
        );

        if (authType === 'pa_auth') {
          // PA_AUTH tokens are opaque tokens, not JWTs - store directly
          localStorage.setItem('access', cookieToken);
          // Note: No need to set auth_type - we use PA_BLID cookie to detect SSO

          // For PA_AUTH tokens, we'll get user info from the backend
          // Fetch complete user information which will validate the token
          if (this.onUserInfoUpdate) {
            this.onUserInfoUpdate();
          }
        } else {
          // PABE_JWT tokens are JWTs - validate structure
          const tokenParts = cookieToken.split('.');
          if (tokenParts.length !== 3) {
            console.warn(
              'Cookie token does not appear to be a valid JWT format'
            );
            return;
          }

          // Validate that each part is non-empty (basic structure check)
          if (tokenParts.some(part => !part || part.trim().length === 0)) {
            console.warn('Cookie token has empty JWT parts');
            return;
          }

          // Validate that it's a proper JWT token by trying to decode it
          const userInfo = extractUserFromJWT(cookieToken);

          if (userInfo && userInfo.userId) {
            // Additional validation: Ensure userId is a reasonable value
            if (
              typeof userInfo.userId !== 'string' &&
              typeof userInfo.userId !== 'number'
            ) {
              console.warn('Cookie token contains invalid user ID format');
              return;
            }

            // Store the token from cookie as the access token
            localStorage.setItem('access', cookieToken);
            // Note: No need to set auth_type - we use PA_BLID cookie to detect SSO

            // Set user ID in Redux store
            if (this.onUserIdUpdate) {
              this.onUserIdUpdate(userInfo.userId);
            }

            // Fetch complete user information
            if (this.onUserInfoUpdate) {
              this.onUserInfoUpdate();
            }
          } else {
            console.warn(
              'Cookie found but does not contain valid user information'
            );
          }
        }
      } catch (error) {
        console.error(
          `Error processing ${authType === 'pa_auth' ? 'PA_AUTH' : 'PABE_JWT'} cookie:`,
          error
        );
        // Don't store anything if there's an error processing the cookie
      }
    }
  }

  /**
   * Clean up the token refresh service
   */
  cleanup() {
    // Remove visibility change listener
    document.removeEventListener(
      'visibilitychange',
      this.visibilityChangeHandler
    );

    // Clear the refresh interval
    this.stopRefreshTimer();
  }

  /**
   * Handle visibility change events
   */
  handleVisibilityChange() {
    this.isPageActive = document.visibilityState === 'visible';

    if (this.isPageActive) {
      // Page is active, check token immediately
      this.checkAndRefreshToken();

      // Restart the refresh timer
      this.startRefreshTimer();
    } else {
      // Page is inactive, stop the refresh timer
      this.stopRefreshTimer();
    }
  }

  /**
   * Start the token refresh timer
   */
  startRefreshTimer() {
    // Clear any existing interval
    this.stopRefreshTimer();

    // Check token every minute
    this.refreshInterval = setInterval(() => {
      this.checkAndRefreshToken();
    }, 60000); // 1 minute
  }

  /**
   * Stop the token refresh timer
   */
  stopRefreshTimer() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  /**
   * Check if token needs refreshing and refresh if needed
   */
  async checkAndRefreshToken() {
    // Skip if refresh is already in progress
    if (this.refreshInProgress) return;

    // Skip if page is not active
    if (!this.isPageActive) return;

    const accessToken = localStorage.getItem('access');
    const refreshToken = localStorage.getItem('refresh');

    // If no access token, nothing to check
    if (!accessToken) return;

    // If token has already expired, handle session expiration
    if (isTokenExpired(accessToken)) {
      this.handleSessionExpired();
      return;
    }

    // Only try to refresh if we have both tokens and access token will expire soon
    if (refreshToken && willTokenExpire(accessToken, 10)) {
      try {
        this.refreshInProgress = true;
        await this.refreshToken(refreshToken);
      } catch (error) {
        console.error('Error refreshing token:', error);
        // If refresh fails and token is expired, handle session expiration
        if (isTokenExpired(accessToken)) {
          this.handleSessionExpired();
        }
      } finally {
        this.refreshInProgress = false;
      }
    }
    // If we only have access token (from cookie) but no refresh token,
    // we can't refresh it, so just let it expire naturally and handle session expiration when it does
  }

  /**
   * Refresh the JWT token
   * @param {string} refreshToken - The refresh token
   * @returns {Promise} Promise that resolves when token is refreshed
   */
  async refreshToken(refreshToken) {
    const formData = new FormData();
    formData.append('refresh', refreshToken);

    const response = await fetch(`${apiEndpoint}/api/token/refresh/`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }

    const data = await response.json();

    if (data.access) {
      // Update the access token in localStorage
      localStorage.setItem('access', data.access);
      // Note: No need to set auth_type - we use PA_BLID cookie to detect SSO

      // Update user info in Redux store
      if (this.onUserInfoUpdate) {
        this.onUserInfoUpdate();
      }

      return data.access;
    } else {
      throw new Error('No access token in response');
    }
  }

  /**
   * Handle session expired
   */
  handleSessionExpired() {
    // Clear tokens from localStorage
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('currentUserId');
    localStorage.removeItem('auth_type');

    // Check if we have PA_AUTH or PABE_JWT cookie for SSO redirect
    const hasPA_AUTH = document.cookie.includes('PA_AUTH');
    const hasPABE_JWT = document.cookie.includes('PABE_JWT');

    if (hasPA_AUTH || hasPABE_JWT) {
      // Clear the invalid SSO cookies by setting them to expire
      if (hasPA_AUTH) {
        document.cookie =
          'PA_AUTH=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.spoton.com;';
      }
      if (hasPABE_JWT) {
        document.cookie =
          'PABE_JWT=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.spoton.com;';
      }

      // Get BFF URL from environment variable
      const bffUrl = process.env.REACT_APP_PROFIT_ASSIST_BFF_URL;

      if (bffUrl) {
        // Redirect to BFF bootstrap endpoint with current page as return URL
        const returnUrl = encodeURIComponent(
          window.location.pathname + window.location.search
        );
        window.location.href = `${bffUrl}/api/v1/auth/bootstrap?redirect=${returnUrl}`;
      } else {
        // Fallback to login page if BFF URL is not configured
        console.warn(
          'REACT_APP_PROFIT_ASSIST_BFF_URL not configured, falling back to login page'
        );
        window.location.href = '/login';
      }
    } else {
      // No SSO cookies, redirect to login page
      window.location.href = '/login';
    }
  }
}

// Create and export a singleton instance
const tokenService = new TokenService();
export default tokenService;
